import React, { useState } from "react";
import {
  Card,
  Space as AntdSpace,
  Button,
  Typography,
  Row,
  Col,
  Skeleton,
  Table,
  Input,
  Divider,
  message,
  DatePicker,
  Select,
} from "antd";
import { DownloadOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  useReportsServiceDownloadBalanceSheetV2,
  useReportsServiceGetBalanceSheetV2,
} from "api/queries";

const { Title, Text } = Typography;
const { Option } = Select;

const PERIOD_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "thisWeek" },
  { label: "This Month", value: "thisMonth" },
  { label: "This Year", value: "thisYear" },
];

const getPeriodRange = (period: string): [dayjs.Dayjs, dayjs.Dayjs] => {
  const now = dayjs();
  switch (period) {
    case "today":
      return [now.startOf("day"), now.endOf("day")];
    case "thisWeek":
      return [now.startOf("week"), now.endOf("week")];
    case "thisMonth":
      return [now.startOf("month"), now.endOf("month")];
    case "thisYear":
      return [now.startOf("year"), now.endOf("year")];
    default:
      return [now.subtract(1, "year"), now];
  }
};

const BalanceSheetReport = () => {
  const company = useSelector((state: any) => state.company);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "year"));
  const [endDate, setEndDate] = useState(dayjs());
  const [period, setPeriod] = useState<string>("");

  const { data, isLoading, isError, refetch } =
    useReportsServiceGetBalanceSheetV2({
      startDate: startDate.format("YYYY-MM-DDTHH:mm:ss"),
      endDate: endDate.format("YYYY-MM-DDTHH:mm:ss"),
    });

  const { mutate: downloadReport, isPending: isDownloadPending } =
    useReportsServiceDownloadBalanceSheetV2();

  const handleDownload = () => {
    downloadReport(
      {
        startDate: startDate.format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
        onError: () => message.error("Failed to download report"),
      }
    );
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    const [start, end] = getPeriodRange(value);
    setStartDate(start);
    setEndDate(end);
  };

  // Format currency with proper accounting notation (negative in parentheses)
  const formatCurrency = (value: number) => {
    const absValue = Math.abs(value);
    const formatted = `${company.currencySymbol || "$"}${absValue.toLocaleString()}`;
    return value < 0 ? `(${formatted})` : formatted;
  };

  // Create columns for the main balance sheet
  const columns = [
    {
      title: "Account",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any) => (
        <Text strong={record.isHeader} style={{ 
          paddingLeft: record.level * 20,
          fontSize: record.isHeader ? 14 : 13,
          color: record.isTotal ? '#1a3353' : 'inherit'
        }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (value: number, record: any) => (
        <Text strong={record.isHeader || record.isTotal} style={{
          color: record.isTotal ? '#1a3353' : 'inherit',
          fontFamily: 'monospace'
        }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
  ];

  // Convert the data to flattened rows for the table
  const getBalanceSheetRows = () => {
    if (!data) return [];
    
    const rows: any[] = [
      {
        key: 'assets-header',
        name: 'ASSETS',
        amount: data.assets[0].total,
        isHeader: true,
        level: 0,
        isTotal: false,
      }
    ];

    // Add asset groups and accounts
    data.assets[0].groups
      .filter(group => !search || group.accounts.some(acc => 
        acc.accountName.toLowerCase().includes(search.toLowerCase())
      ))
      .forEach(group => {
        // Add group header
        rows.push({
          key: `group-${group.groupName}`,
          name: group.groupName.replace(/_/g, " "),
          amount: group.total,
          isHeader: true,
          level: 1,
          isTotal: false,
        });

        // Add accounts in the group
        group.accounts
          .filter(acc => !search || 
            acc.accountName.toLowerCase().includes(search.toLowerCase())
          )
          .forEach(acc => {
            rows.push({
              key: `acc-${acc.accountId}`,
              name: acc.accountName,
              amount: acc.balance,
              isHeader: false,
              level: 2,
              isTotal: false,
            });
          });
      });

    // Add asset total
    rows.push({
      key: 'assets-total',
      name: 'TOTAL ASSETS',
      amount: data.assets[0].total,
      isHeader: false,
      level: 0,
      isTotal: true,
    });

    // Add separator
    rows.push({
      key: 'separator',
      name: '',
      amount: 0,
      isHeader: false,
      level: 0,
      isTotal: false,
    });

    // Add liabilities header
    rows.push({
      key: 'liabilities-header',
      name: 'LIABILITIES',
      amount: data.liabilities[0].total,
      isHeader: true,
      level: 0,
      isTotal: false,
    });

    // Add liability groups and accounts
    data.liabilities[0].groups
      .filter(group => !search || group.accounts.some(acc => 
        acc.accountName.toLowerCase().includes(search.toLowerCase())
      ))
      .forEach(group => {
        // Add group header
        rows.push({
          key: `group-${group.groupName}`,
          name: group.groupName.replace(/_/g, " "),
          amount: group.total,
          isHeader: true,
          level: 1,
          isTotal: false,
        });

        // Add accounts in the group
        group.accounts
          .filter(acc => !search || 
            acc.accountName.toLowerCase().includes(search.toLowerCase())
          )
          .forEach(acc => {
            rows.push({
              key: `acc-${acc.accountId}`,
              name: acc.accountName,
              amount: acc.balance,
              isHeader: false,
              level: 2,
              isTotal: false,
            });
          });
      });

    // Add liabilities total
    rows.push({
      key: 'liabilities-total',
      name: 'TOTAL LIABILITIES',
      amount: data.liabilities[0].total,
      isHeader: false,
      level: 0,
      isTotal: true,
    });

    // Add equity header
    rows.push({
      key: 'equity-header',
      name: 'EQUITY',
      amount: data.equity[0].total,
      isHeader: true,
      level: 0,
      isTotal: false,
    });

    // Add equity groups and accounts
    data.equity[0].groups
      .filter(group => !search || group.accounts.some(acc => 
        acc.accountName.toLowerCase().includes(search.toLowerCase())
      ))
      .forEach(group => {
        // Add group header
        rows.push({
          key: `group-${group.groupName}`,
          name: group.groupName.replace(/_/g, " "),
          amount: group.total,
          isHeader: true,
          level: 1,
          isTotal: false,
        });

        // Add accounts in the group
        group.accounts
          .filter(acc => !search || 
            acc.accountName.toLowerCase().includes(search.toLowerCase())
          )
          .forEach(acc => {
            rows.push({
              key: `acc-${acc.accountId}`,
              name: acc.accountName,
              amount: acc.balance,
              isHeader: false,
              level: 2,
              isTotal: false,
            });
          });
      });

    // Add equity total
    rows.push({
      key: 'equity-total',
      name: 'TOTAL EQUITY',
      amount: data.equity[0].total,
      isHeader: false,
      level: 0,
      isTotal: true,
    });

    // Add liabilities + equity total
    rows.push({
      key: 'liabilities-equity-total',
      name: 'TOTAL LIABILITIES AND EQUITY',
      amount: data.liabilities[0].total + data.equity[0].total,
      isHeader: false,
      level: 0,
      isTotal: true,
    });

    return rows;
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ duration: 0.5 }}
      style={{ background: "#fff", padding: 24, borderRadius: 2 }}
    >
      <Row style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Title level={2} style={{ margin: 0, color: '#1a3353' }}>Balance Sheet</Title>
          <Text type="secondary">
            As of {endDate ? endDate.format("MMMM DD, YYYY") : ""}
          </Text>
        </Col>
      </Row>

      <Row style={{ marginBottom: 24 }} justify="space-between" align="middle">
        <Col>
          <AntdSpace>
            <Select
              value={period}
              onChange={handlePeriodChange}
              placeholder="Period"
              style={{ width: 120 }}
              allowClear
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                if (date) {
                  setStartDate(date);
                  setPeriod("");
                }
              }}
              style={{ width: 120 }}
              placeholder="From"
              allowClear={false}
            />
            <DatePicker
              value={endDate}
              onChange={(date) => {
                if (date) {
                  setEndDate(date);
                  setPeriod("");
                }
              }}
              style={{ width: 120 }}
              placeholder="To"
              allowClear={false}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </AntdSpace>
        </Col>
        <Col>
          <AntdSpace>
            <Input.Search
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter accounts"
              style={{ width: 200 }}
              allowClear
            />
            <Button 
              icon={<PrinterOutlined />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Export
            </Button>
          </AntdSpace>
        </Col>
      </Row>

      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <div style={{ 
            background: '#f5f7fa', 
            padding: '16px 24px', 
            borderLeft: '4px solid #1a3353',
            borderRadius: 2
          }}>
            <Row justify="space-between">
              <Col>
                <Text strong>{company.companyName || 'Company Name'}</Text>
              </Col>
              <Col>
                <Text strong>
                  Balance Sheet | {data?.reportInfo.startDate} - {data?.reportInfo.endDate}
                </Text>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 20 }} />
      ) : isError ? (
        <Card><Text type="danger">Failed to load data.</Text></Card>
      ) : data ? (
        <div className="financial-report" style={{ 
          fontFamily: "'Times New Roman', Times, serif",
        }}>
          <Table
            columns={columns}
            dataSource={getBalanceSheetRows()}
            pagination={false}
            bordered={true}
            size="middle"
            rowClassName={(record) => {
              if (record.key === 'separator') return 'separator-row';
              if (record.isTotal) return 'total-row';
              if (record.isHeader) return 'header-row';
              return '';
            }}
            className="balance-sheet-table"
          />

          <Row style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card 
                bordered 
                size="small" 
                title={<Text strong>Balance Check</Text>}
                style={{ background: '#f9f9f9' }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Total Assets:</Text>
                      <Text style={{ fontFamily: 'monospace' }}>{formatCurrency(data.summary.totalAssets)}</Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Total Liabilities:</Text>
                      <Text style={{ fontFamily: 'monospace' }}>{formatCurrency(data.summary.totalLiabilities)}</Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Total Equity:</Text>
                      <Text style={{ fontFamily: 'monospace' }}>{formatCurrency(data.summary.totalEquity)}</Text>
                    </div>
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <Row>
                  <Col span={24}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text strong>Balance Check (A = L + E):</Text>
                      <Text strong style={{ fontFamily: 'monospace' }}>{formatCurrency(data.summary.balanceCheck)}</Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      ) : null}

      <style>{`
        .balance-sheet-table .separator-row {
          height: 1px;
          background: #f0f0f0;
        }
        .balance-sheet-table .separator-row td {
          padding: 0;
          height: 12px !important;
          border-bottom: 1px solid #d9d9d9;
        }
        .balance-sheet-table .header-row {
          background-color: #f5f7fa;
        }
        .balance-sheet-table .total-row {
          background-color: #fafafa;
          border-top: 1px solid #d9d9d9;
          border-bottom: 1px double #d9d9d9 !important;
        }
        .balance-sheet-table .ant-table-cell {
          vertical-align: middle;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          .financial-report, .financial-report * {
            visibility: visible;
          }
          .financial-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default BalanceSheetReport;
